import React, { Dispatch, SetStateAction, useEffect, useContext } from 'react';
import { createStyles, Theme, makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import download from 'downloadjs';
import { ToolBarContext, MeetingContext } from '../../../../App';
import mergeImages from 'merge-images';
import { axClient, axClientUpload } from '../../../../axiosClient';
import TextField from '@material-ui/core/TextField';

interface IcaptureOptionsModal {
  setShow: Dispatch<SetStateAction<boolean>>;
  show: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    sampleScreenshot: {
      width: 'auto',
      height: '200px',
    },
    imageContainer: {
      display: 'flex',
      justifyContent: 'center',
    },
    jobsButton: {
      backgroundColor: '#00A4EF',
      color: 'white',
      '&:hover': {
        backgroundColor: '#00A4EF',
      },
    },
    noteInput: {
      display: 'flex',
      justifyContent: 'center',
    },
    textField: {
      width: '70%',
      margin: '0',
      '&:label': {
        color: 'red',
      },
    },
  })
);

export default function CaptureOptionsModal({ setShow, show }: IcaptureOptionsModal) {
  const classes = useStyles();
  const [picture, setPicture] = React.useState('');
  const { setSnackBar, setSnackBarText } = useContext(ToolBarContext);
  const { job } = useContext(MeetingContext);
  const [note, setNote] = React.useState('');
  const [saveButtonClicked, setSaveButtonClicked] = React.useState(false);

  const handleClose = () => {
    setShow(false);
  };

  const capture = () => {
    // video source
    var mainVideoContainer = document.getElementById('main-container');
    let video = mainVideoContainer.getElementsByTagName('video')[0];
    let videoWidth;
    let videoHeight;

    // sketch canvas source
    let hostSketch = document.getElementById('sketchArea');
    let sketchsource = hostSketch.getElementsByClassName('lower-canvas')[0] as HTMLCanvasElement;

    //sketch canvas
    let sketchCanvas = document.createElement('canvas');
    let sketchContext = sketchCanvas.getContext('2d');
    let videoCanvas = document.createElement('canvas');

    if (video) {
      videoWidth = video.videoWidth;
      videoHeight = video.videoHeight;
      sketchCanvas.width = videoWidth;
      sketchCanvas.height = videoHeight;
      sketchContext.drawImage(sketchsource, 0, 0, videoWidth, videoHeight);

      //video Canvas
      videoCanvas.width = videoWidth;
      videoCanvas.height = videoHeight;
      const ctx2 = videoCanvas.getContext('2d');
      ctx2.drawImage(video, 0, 0);
    } else {
      sketchContext.drawImage(sketchsource, 0, 0);
    }
    // Merging video canvas and sketch canvas
    if (!video) setPicture(sketchCanvas.toDataURL());
    else mergeImages([videoCanvas.toDataURL(), sketchCanvas.toDataURL()]).then(mergedImage => setPicture(mergedImage));
  };

  const downloadToPC = () => {
    download(picture, `${note ? note : 'SCREENSHOT_'}` + Date.now() + '.png');
  };

  function DataURIToBlob(dataURI: string) {
    const splitDataURI = dataURI.split(',');
    const byteString = splitDataURI[0].indexOf('base64') >= 0 ? atob(splitDataURI[1]) : decodeURI(splitDataURI[1]);
    const mimeString = splitDataURI[0].split(':')[1].split(';')[0];
    const ia = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);

    return new Blob([ia], { type: mimeString });
  }

  const saveToNotes = () => {
    setSaveButtonClicked(true);
    if (note) {
      let image = DataURIToBlob(picture);
      let formData = new FormData();
      formData.append('attachment_type', 'JOB_NOTE');
      formData.append('attachment', image, 'image.png');
      axClientUpload
        .post('misc/upload', formData)
        .then(response => {
          let attachmentData = response.data.data;
          let attach = {};
          attach['attachment'] = attachmentData.attachment_path;
          attach['attachment_size'] = attachmentData.attachment_size;
          attach['attachment_type'] = 'IMAGE';
          axClientUpload
            .post(`jobs/${job.job_uid}/note`, {
              note: {
                is_private: false,
                attachments: [attach],
                note: note ? note : 'Screenshot from Zuper Connect',
                note_type: 'IMAGE',
              },
            })
            .then(() => {
              axClient
                .post('/event', {
                  event_type: 'snapshot_captured',
                  event_data: { snapshot_url: attachmentData.attachment_path },
                })
                .then(() => {
                  setSnackBar(true);
                  setSnackBarText('Note added to Job');
                  handleClose();
                });
            })
            .catch(err => {
              throw err;
            });
        })
        .catch(err => {
          throw err;
        });
    }
  };

  //To set image source after the modal mounted
  useEffect(() => {
    capture();
  }, []);

  return (
    <Dialog
      disableBackdropClick
      disableEscapeKeyDown
      fullWidth={true}
      maxWidth={'sm'}
      open={show}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">Save Snapshot</DialogTitle>
      <DialogContent dividers>
        <DialogContentText id="alert-dialog-description">
          Do you want to save this snapshot to the Job's note?
        </DialogContentText>
        <DialogContent className={classes.noteInput}>
          <TextField
            autoFocus
            id="filled-full-width"
            label={
              saveButtonClicked && !note ? (
                <span style={{ color: 'red' }}>Please enter text to save Job Note</span>
              ) : (
                'Note'
              )
            }
            className={classes.textField}
            placeholder="Enter your Note text here"
            margin="normal"
            value={note}
            onChange={e => setNote(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            variant="filled"
          />
        </DialogContent>
        <DialogContent className={classes.imageContainer}>
          <img id="sample_snapShot" alt="sampleScreenshot" className={classes.sampleScreenshot} src={picture} />
        </DialogContent>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" className={classes.jobsButton} onClick={saveToNotes}>
          Save to Job Notes
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            handleClose();
            downloadToPC();
          }}
        >
          Download to my PC
        </Button>
        <Button color="secondary" variant="contained" onClick={handleClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
